import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { ArrowLeft, Calendar, User, Mail, Loader2, Save, XCircle, Banknote } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { bookingsApi, type BookingListResponse } from "@/lib/api";
import { formatNaira } from "@/data/venues";

export const Route = createFileRoute("/_user/bookings/$id")({ component: ModifyBookingPage });

function ModifyBookingPage() {
  const { id } = Route.useParams();
  const router = useRouter();
  const [booking, setBooking] = useState<BookingListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [guestName, setGuestName] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [specialRequests, setSpecialRequests] = useState("");
  const [accountName, setAccountName] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [bankName, setBankName] = useState("");

  useEffect(() => { fetchBooking(); }, [id]);

  async function fetchBooking() {
    setLoading(true);
    try {
      const b = await bookingsApi.getById(id);
      setBooking(b);
      setStartDate(b.startDate.slice(0, 10));
      setEndDate(b.endDate.slice(0, 10));
      setGuestName(b.guestName || "");
      setGuestEmail(b.guestEmail || "");
      setSpecialRequests(b.specialRequests || "");
    } catch (e: any) { toast.error(e?.message || "Failed to load booking"); }
    finally { setLoading(false); }
  }

  async function handleSave() {
    setSaving(true);
    try {
      await bookingsApi.update(id, {
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        guestName: guestName || undefined,
        guestEmail: guestEmail || undefined,
        specialRequests: specialRequests || undefined,
      });
      toast.success("Booking updated. Manager approval may be required for significant changes.");
      fetchBooking();
    } catch (e: any) { toast.error(e?.message || "Update failed"); }
    finally { setSaving(false); }
  }

  async function handleCancel() {
    try {
      await bookingsApi.cancel(id, {
        accountName: accountName.trim() || undefined,
        accountNumber: accountNumber.trim() || undefined,
        bankName: bankName.trim() || undefined,
      });
      toast.success("Booking cancelled. Refund will be processed to the provided account.");
      router.navigate({ to: "/_user/bookings" });
    } catch (e: any) { toast.error(e?.message || "Cancel failed"); }
  }

  if (loading) return <div className="flex items-center justify-center h-96"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>;
  if (!booking) return <div className="p-8 text-center text-muted-foreground">Booking not found.</div>;

  const isEditable = !["cancelled", "checked_out", "checked_in"].includes(booking.status);

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-6">
      <Button variant="ghost" onClick={() => router.history.back()}><ArrowLeft className="mr-2 h-4 w-4" /> Back</Button>
      <h1 className="text-2xl font-bold">Modify Booking</h1>
      <p className="text-muted-foreground">{booking.venueName} — {booking.bookingRef}</p>

      <section className="border rounded-lg p-5 bg-card space-y-4">
        <h2 className="font-semibold flex items-center gap-2"><Calendar className="h-5 w-5 text-primary" /> Dates</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm text-muted-foreground">Check-in</label>
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} disabled={!isEditable} />
          </div>
          <div>
            <label className="text-sm text-muted-foreground">Check-out</label>
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} disabled={!isEditable} />
          </div>
        </div>
      </section>

      <section className="border rounded-lg p-5 bg-card space-y-4">
        <h2 className="font-semibold flex items-center gap-2"><User className="h-5 w-5 text-primary" /> Guest Details</h2>
        <div className="space-y-3">
          <div>
            <label className="text-sm text-muted-foreground">Name</label>
            <Input value={guestName} onChange={(e) => setGuestName(e.target.value)} disabled={!isEditable} />
          </div>
          <div>
            <label className="text-sm text-muted-foreground">Email</label>
            <Input type="email" value={guestEmail} onChange={(e) => setGuestEmail(e.target.value)} disabled={!isEditable} />
          </div>
          <div>
            <label htmlFor="specialRequests" className="text-sm text-muted-foreground">Special Requests</label>
            <textarea id="specialRequests" placeholder="Any special requests?" value={specialRequests} onChange={(e) => setSpecialRequests(e.target.value)} disabled={!isEditable} className="w-full border rounded-lg p-3 min-h-[80px]" />
          </div>
        </div>
      </section>

      <section className="border rounded-lg p-5 bg-card space-y-3">
        <h2 className="font-semibold">Payment Summary</h2>
        <div className="flex justify-between text-sm"><span className="text-muted-foreground">Total</span><span className="font-medium">{formatNaira(booking.totalAmount)}</span></div>
        <div className="flex justify-between text-sm"><span className="text-muted-foreground">Per night</span><span>{formatNaira(booking.pricePerNight || 0)}</span></div>
        <Separator />
        <div className="flex justify-between text-sm"><span className="text-muted-foreground">Payment status</span><span>{booking.paymentStatus}</span></div>
      </section>

      {isEditable && (
        <section className="border rounded-lg p-5 bg-card space-y-4">
          <h2 className="font-semibold flex items-center gap-2"><Banknote className="h-5 w-5 text-primary" /> Refund Account Details</h2>
          <p className="text-sm text-muted-foreground">If your booking is eligible for a refund, provide the bank account where it should be sent.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-muted-foreground">Account Name</label>
              <Input value={accountName} onChange={(e) => setAccountName(e.target.value)} placeholder="e.g. John Doe" />
            </div>
            <div>
              <label className="text-sm text-muted-foreground">Bank Name</label>
              <Input value={bankName} onChange={(e) => setBankName(e.target.value)} placeholder="e.g. First Bank" />
            </div>
            <div className="md:col-span-2">
              <label className="text-sm text-muted-foreground">Account Number</label>
              <Input value={accountNumber} onChange={(e) => setAccountNumber(e.target.value)} placeholder="10 digit account number" />
            </div>
          </div>
        </section>
      )}

      {isEditable && (
        <div className="flex flex-wrap gap-3">
          <Button onClick={handleSave} disabled={saving}>{saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />} Save Changes</Button>
          <Button variant="destructive" onClick={handleCancel}><XCircle className="h-4 w-4 mr-2" /> Cancel Booking</Button>
        </div>
      )}
    </div>
  );
}
